import { createFileRoute, Link } from "@tanstack/react-router";
import { EsLiveRateSnapshot } from "@/components/rates/EsLiveRateSnapshot";
import { SiteShell, PageHero } from "@/components/site/SiteShell";
import { Section, SectionHeader, FeatureGrid, FAQ, CTABand, StatRow } from "@/components/site/Blocks";
import { TLDR } from "@/components/site/SeoBlocks";
import { buildHead } from "@/lib/seo-head";
import { localizedPath } from "@/lib/i18n/routes";
import { useI18n } from "@/lib/i18n/useLocale";
import { products } from "@/data/products";
import { states } from "@/data/states";
import heroAsset from "@/assets/hero-dscr-investment-property.jpg.asset.json";

const faqItems = [
  {
    q: "¿Qué es un préstamo DSCR y cómo funciona?",
    a: "Un préstamo DSCR (Debt Service Coverage Ratio) califica a la propiedad según el flujo de caja de la renta, no según los ingresos personales del prestatario. Los prestamistas dividen la renta mensual bruta entre el pago total mensual de la deuda (PITIA). Si la relación cumple el mínimo del programa, el préstamo puede calificar sin comprobantes de ingresos tradicionales.",
  },
  {
    q: "¿Quién califica para un préstamo DSCR?",
    a: "Inversionistas inmobiliarios, LLCs, sociedades y corporaciones que poseen o compran propiedades de alquiler no ocupadas por el propietario. La aprobación se basa en la capacidad de la propiedad para generar renta, el crédito del prestatario y las reservas líquidas.",
  },
  {
    q: "¿Se requieren declaraciones de impuestos o ingresos personales?",
    a: "No. Los préstamos DSCR se suscriben como préstamos con fines comerciales (business-purpose) en propiedades de inversión, por lo que generalmente no se requieren declaraciones de impuestos, W-2s o talones de pago personales.",
  },
  {
    q: "¿Puedo cerrar a nombre de una LLC o entidad comercial?",
    a: "Sí. Es común aceptar vestimenta en LLC, LP, S-Corp, C-Corp y Series LLC en programas elegibles, sin prima de tasa adicional por usar entidad.",
  },
  {
    q: "¿En qué estados están disponibles los préstamos DSCR?",
    a: "Simply Approved Mortgages tiene licencia en Florida y Colorado para préstamos residenciales a consumidores. Los préstamos DSCR y con fines comerciales para inversionistas están disponibles en jurisdicciones adicionales donde la ley lo permite. Contáctenos para confirmar disponibilidad en su estado.",
  },
];

export const Route = createFileRoute("/es/")({
  head: () =>
    buildHead({
      title: "Préstamos DSCR para Inversionistas | Simply Approved Mortgages",
      description:
        "Préstamos DSCR con calificación por renta de la propiedad — sin declaraciones de impuestos ni ingresos personales. Con licencia en FL y CO; préstamos con fines comerciales en jurisdicciones adicionales.",
      url: "/es",
      locale: "es",
      keywords:
        "préstamos DSCR, préstamos con fines comerciales, préstamos para propiedades de inversión, préstamos para inversionistas inmobiliarios, préstamos DSCR Florida, préstamos DSCR Colorado, préstamos LLC, préstamos sin declaración de impuestos, préstamos para extranjeros, préstamos construcción, préstamos fix and flip, préstamos Airbnb, préstamos Section 8",
      image: heroAsset.url,
      imageAlt: "Propiedad de inversión financiada con DSCR al atardecer",
      breadcrumbs: [{ name: "Inicio", url: "/es" }],
      service: { name: "Préstamos DSCR y con fines comerciales para propiedades de inversión" },
      faqs: faqItems,
      speakableSelectors: [".page-hero", ".faq"],
    }),
  component: Home,
});

function Home() {
  const { locale } = useI18n();
  return (
    <SiteShell productLabel="Inversionista">
      <PageHero
        eyebrow="Simply Approved Mortgages · Escritorio DSCR"
        title="Préstamos DSCR y financiamiento para propiedades de inversión"
        subtitle="Califique usando el flujo de caja de la propiedad — no ingresos personales ni declaraciones de impuestos. Ocho soluciones de préstamos con fines comerciales: DSCR, Extranjeros, Construcción, Fix & Flip, Bridge, Airbnb/Alquiler a corto plazo, Section 8 y Préstamos para Portafolio. Hasta 80% LTV en programas DSCR elegibles, sin declaraciones de impuestos ni ingresos personales. Con licencia en Florida y Colorado; disponibles como transacciones comerciales en jurisdicciones adicionales donde se permite."
        primaryLabel="Iniciar Mi Aprobación"
        image={{
          src: heroAsset.url,
          alt: "Propiedad de inversión financiada con DSCR — Simply Approved Mortgages",
        }}
      />
      <Section>
        <TLDR
          id="tldr"
          points={[
            "Los préstamos DSCR califican por la relación renta/PITIA, no por W-2s o declaraciones de impuestos.",
            "Cierre a nombre de LLC, LP o S-Corp sin prima de tasa en programas elegibles.",
            "Programas desde fijos a 30 años hasta solo intereses con hasta 80% LTV y montos hasta $5M.",
            "Con licencia en Florida y Colorado; préstamos comerciales disponibles en nuestra área de servicio donde se permite.",
            "Programas DSCR elegibles hasta 80% LTV sin ingresos personales ni declaraciones de impuestos — la calificación se basa en la renta de la propiedad, el crédito y las reservas.",
            "Revisión inicial del escenario generalmente el mismo día hábil una vez recibida la solicitud completa, autorización de crédito y documentos de la propiedad/entidad.",
          ]}
        />
      </Section>

      <Section>
        <EsLiveRateSnapshot heading="Tasas DSCR de hoy, de un vistazo" intro="Una muestra en vivo de precios para inversionistas; cotice su escenario para ver números específicos." />
      </Section>

      <Section>
        <SectionHeader
          title="Por qué los inversionistas eligen Simply Approved Mortgages"
          center
        />
        <div className="mt-10">
          <StatRow
            items={[
              { label: "Con licencia en", value: "FL · CO" },
              { label: "Área de servicio", value: `${states.length} estados + DC` },
              { label: "Max LTV (DSCR)", value: "Hasta 80%" },
              { label: "Documentos de ingresos", value: "No requeridos" },
              { label: "Programas para inversionistas", value: "8" },
              { label: "DSCR mín.", value: "0.75" },
              { label: "Monto máx.", value: "$5M" },
              { label: "Vestimenta", value: "LLC / LP / Corp" },
            ]}
          />
          <p className="mt-3 text-xs text-muted-foreground">
            Los máximos LTV, DSCR mínimo y montos de préstamo mostrados son máximos de programa en
            préstamos DSCR elegibles con fines comerciales. Los términos reales dependen del crédito,
            tipo de propiedad, reservas, avalúo y aprobación de suscripción. No se requieren
            declaraciones de impuestos ni ingresos personales en programas DSCR elegibles; aún se
            requieren crédito, reservas y documentación de la propiedad.
          </p>
        </div>
      </Section>

      <Section>
        <SectionHeader eyebrow="Cobertura" title={`Préstamos DSCR en ${states.length} estados más Washington, DC`} />
        <div className="mt-8 flex flex-wrap gap-2">
          {states.map((s) => (
            <Link
              key={s.slug}
              to="/es/donde-prestamos"
              className="rounded-full border border-border bg-card px-3 py-1.5 text-xs font-semibold text-foreground transition-colors hover:border-brand hover:text-brand"
            >
              {s.name}
            </Link>
          ))}
        </div>
      </Section>

      <Section>
        <SectionHeader title="Nuestros programas para inversionistas" center />
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {products.map((p) => (
            <Link
              key={p.slug}
              to={localizedPath(`/${p.slug}`, locale)}
              className="group rounded-2xl border border-border bg-card p-6 shadow-sm transition hover:border-brand hover:shadow-md"
            >
              <h3 className="font-display text-lg font-bold text-foreground group-hover:text-brand">{p.name}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{p.description}</p>
            </Link>
          ))}
        </div>
      </Section>

      <CTABand
        title="Hable con un oficial de préstamos para inversionistas"
        body="Envíenos su escenario. Respuestas reales y cotizaciones reales."
      />
    </SiteShell>
  );
}
